import React, { useState, useEffect } from 'react';
import { 
  Trash2, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Mail,
  Calendar,
  User,
  Heart
} from 'lucide-react';
import {
  Layout,
  LoadingSpinner,
  Alert,
  EmptyState,
  ConfirmDialog
} from '../../components';
import { userService } from '../../services';
import { logger } from '../../utils';

interface PendingDeletion {
  id: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  deletionScheduledFor: Date;
  deletionRequestedAt: Date;
  deletionReason: string;
  daysRemaining: number;
  angelCount: number;
  strayyosAffected: number;
  isDue: boolean;
  createdAt: Date;
}

export const DeletionsPage: React.FC = () => {
  const [deletions, setDeletions] = useState<PendingDeletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [executeConfirm, setExecuteConfirm] = useState<PendingDeletion | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<PendingDeletion | null>(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    fetchDeletions();
  }, []);

  const fetchDeletions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getPendingDeletions();
      setDeletions(response.deletions || []);
      logger.info(`Loaded ${response.count} pending deletions`);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to load pending deletions';
      setError(message);
      logger.error('Failed to fetch deletions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteDeletion = async (deletion: PendingDeletion) => {
    try {
      setError(null);
      setSuccess(null);
      await userService.executeDeletion(deletion.id);
      setSuccess(`Account deleted successfully for ${deletion.email || deletion.phone}`);
      setExecuteConfirm(null);
      fetchDeletions();
      logger.info(`Executed deletion for user ${deletion.id}`);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to execute deletion';
      setError(message);
      logger.error('Failed to execute deletion:', err);
    }
  };

  const handleCancelDeletion = async (deletion: PendingDeletion) => {
    try {
      setError(null);
      setSuccess(null);
      await userService.cancelDeletion(deletion.id);
      setSuccess(`Deletion request cancelled for ${deletion.email || deletion.phone}`);
      setCancelConfirm(null);
      fetchDeletions();
      logger.info(`Cancelled deletion for user ${deletion.id}`);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to cancel deletion';
      setError(message);
      logger.error('Failed to cancel deletion:', err);
    }
  };

  const handleSendReminders = async (days: number = 7) => {
    try {
      setSendingReminders(true);
      setError(null);
      setSuccess(null);
      await userService.sendDeletionReminders(days);
      setSuccess(`Sent ${days}-day reminders to users with pending deletions`);
      logger.info(`Sent ${days}-day deletion reminders`);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to send reminders';
      setError(message);
      logger.error('Failed to send reminders:', err);
    } finally {
      setSendingReminders(false);
    }
  };

  const getUrgencyColor = (daysRemaining: number) => {
    if (daysRemaining <= 0) return 'text-red-600 bg-red-50';
    if (daysRemaining <= 7) return 'text-orange-600 bg-orange-50';
    if (daysRemaining <= 14) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getUrgencyIcon = (daysRemaining: number) => {
    if (daysRemaining <= 0) return <AlertTriangle className="w-5 h-5 text-red-600" />;
    if (daysRemaining <= 7) return <Clock className="w-5 h-5 text-orange-600" />;
    return <Clock className="w-5 h-5 text-gray-600" />;
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trash2 className="w-7 h-7" />
              Account Deletion Requests
            </h1>
            <p className="text-gray-600 mt-1">
              Manage user account deletion requests with 30-day grace period
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => handleSendReminders(7)}
              disabled={sendingReminders || deletions.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail className="w-4 h-4" />
              {sendingReminders ? 'Sending...' : 'Send 7-Day Reminders'}
            </button>
            <button
              onClick={fetchDeletions}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}
        {success && (
          <Alert
            variant="success"
            message={success}
            onClose={() => setSuccess(null)}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pending</p>
                <p className="text-2xl font-bold text-gray-900">{deletions.length}</p>
              </div>
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Due Now</p>
                <p className="text-2xl font-bold text-red-600">
                  {deletions.filter(d => d.isDue).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Due in 7 Days</p>
                <p className="text-2xl font-bold text-orange-600">
                  {deletions.filter(d => d.daysRemaining > 0 && d.daysRemaining <= 7).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-orange-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Strayyos Affected</p>
                <p className="text-2xl font-bold text-gray-900">
                  {deletions.reduce((sum, d) => sum + d.strayyosAffected, 0)}
                </p>
              </div>
              <Heart className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Deletions List */}
        {deletions.length === 0 ? (
          <EmptyState
            icon={<CheckCircle className="w-12 h-12 text-gray-400" />}
            title="No Pending Deletions"
            message="There are no account deletion requests at this time."
          />
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scheduled
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Remaining
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Impact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deletions.map((deletion) => (
                    <tr key={deletion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-500" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {deletion.name || 'Unnamed User'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {deletion.email || deletion.phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(deletion.deletionRequestedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(deletion.deletionScheduledFor)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getUrgencyColor(deletion.daysRemaining)}`}>
                          {getUrgencyIcon(deletion.daysRemaining)}
                          {deletion.isDue ? 'DUE NOW' : `${deletion.daysRemaining} days`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {deletion.angelCount} Angel{deletion.angelCount !== 1 ? 's' : ''}
                          </div>
                          <div className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {deletion.strayyosAffected} Strayyo{deletion.strayyosAffected !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        <div className="truncate" title={deletion.deletionReason}>
                          {deletion.deletionReason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setExecuteConfirm(deletion)}
                            className="text-red-600 hover:text-red-900 flex items-center gap-1"
                            title="Execute Deletion"
                          >
                            <Trash2 className="w-4 h-4" />
                            Execute
                          </button>
                          <button
                            onClick={() => setCancelConfirm(deletion)}
                            className="text-green-600 hover:text-green-900 flex items-center gap-1"
                            title="Cancel Deletion"
                          >
                            <XCircle className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Execute Confirmation Dialog */}
        <ConfirmDialog
          isOpen={!!executeConfirm}
          title="Execute Account Deletion"
          message={`Are you sure you want to permanently delete the account for ${executeConfirm?.email || executeConfirm?.phone}? This will transfer ${executeConfirm?.strayyosAffected || 0} Strayyo(s) to ketan@strayyo.com and delete all user data. This action cannot be undone.`}
          confirmText="Delete Account"
          confirmButtonVariant="danger"
          onConfirm={() => executeConfirm && handleExecuteDeletion(executeConfirm)}
          onClose={() => setExecuteConfirm(null)}
        />

        {/* Cancel Confirmation Dialog */}
        <ConfirmDialog
          isOpen={!!cancelConfirm}
          title="Cancel Deletion Request"
          message={`Cancel the deletion request for ${cancelConfirm?.email || cancelConfirm?.phone}? The user's account will remain active and they can continue using the platform normally.`}
          confirmText="Cancel Deletion Request"
          confirmButtonVariant="primary"
          onConfirm={() => cancelConfirm && handleCancelDeletion(cancelConfirm)}
          onClose={() => setCancelConfirm(null)}
        />
      </div>
    </Layout>
  );
};

export default DeletionsPage;
